import clsx from 'clsx';
import { forwardRef, type DetailedHTMLProps, type InputHTMLAttributes, useMemo } from 'react';

import { Label, calculateInputWidth } from '../shared';

export type TextInputProps = {
  label?: string;
  value?: string | number;
  icon?: React.ComponentType<{ className?: string }>;
  fullWidth?: boolean; // New prop to control width behavior
} & Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'type'>;

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, className, disabled, value, icon: Icon, placeholder, fullWidth = false, ...rest },
  ref,
) {
  // Calculate dynamic width based on placeholder text (only when not fullWidth)
  const dynamicWidth = useMemo(() => {
    if (fullWidth) return 'w-full';
    return calculateInputWidth(placeholder, !!Icon);
  }, [placeholder, Icon, fullWidth]);

  return (
    <Label text={label} className={fullWidth ? 'w-full' : ''}>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center">
            <Icon className="h-4 w-4 text-gray-400" />
          </div>
        )}
        <input
          {...rest}
          type="text"
          value={value}
          ref={ref}
          disabled={disabled}
          placeholder={placeholder}
          className={clsx(
            'flex items-center rounded-md border bg-white text-sm',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'outline-none transition-[color,box-shadow]',
            'placeholder:text-muted-foreground',
            'h-[40px]', // Match select component height exactly
            {
              'cursor-not-allowed opacity-50': disabled,
              'pl-10 pr-3': Icon, // Add left padding when icon is present, right padding
              'px-3': !Icon, // Default padding when no icon
            },
            // Apply className first, then width to ensure width takes precedence
            className,
            dynamicWidth, // Dynamic width based on placeholder or full width - applied last
          )}
        />
      </div>
    </Label>
  );
});
